import {DomainObject} from 'shared/store';
import {t} from 'shared/core';
import { observable, computed, makeObservable } from 'mobx';
import TimeOffType from './TimeOffType';
import {rangeDate, calendarDate, absoluteDateFormatter} from 'shared/tools';
import User from 'main/stores/users/User';
import {Employee} from 'main/stores/employees';
import {AttachedDocument} from 'main/stores/documents';
import TimeOffRequestAssignment from './TimeOffRequestAssignment';
import _ from 'lodash';
import moment from 'moment';

const MAX_YEAR = 2999;
const MAX_DURATION_YEARS = 5;
const FIRST_APPROVAL_STAGE = 1;
const SECOND_APPROVAL_STAGE = 2;

class TimeOffRequest extends DomainObject {
  id;
  createdAt;
  startDate;
  endDate;
  status;
  amount;
  timeOffType;
  upcoming = false;
  notes;
  attachments;

  actionNotes;
  firstStageNotes;
  firstStageActionedByUser;
  firstStageActionedAt;
  createdByUser;
  secondStageNotes;
  secondStageActionedByUser;
  secondStageActionedAt;
  firstStageStatus;
  secondStageStatus;
  currentAssignedUser;
  currentApprovalStage;
  totalApprovalStages;
  actionedAt;

  employee;
  delegateAssignmentsToUser;

  assignments;
  currentAssignments;

  _relations = {
    timeOffType: TimeOffType,
    firstStageActionedByUser: User,
    secondStageActionedByUser: User,
    assignments: [TimeOffRequestAssignment],
    currentAssignments: [TimeOffRequestAssignment],
    attachments: [AttachedDocument],
    currentlyDelegatedByUser: User,
    createdByUser: User,
    employee: Employee,
    delegateAssignmentsToUser: User,
    _dates: ['startDate', 'endDate']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      createdAt: observable,
      startDate: observable,
      endDate: observable,
      status: observable,
      amount: observable,
      timeOffType: observable,
      upcoming: observable,
      notes: observable,
      attachments: observable,
      actionNotes: observable,
      firstStageNotes: observable,
      firstStageActionedByUser: observable,
      firstStageActionedAt: observable,
      createdByUser: observable,
      secondStageNotes: observable,
      secondStageActionedByUser: observable,
      secondStageActionedAt: observable,
      firstStageStatus: observable,
      secondStageStatus: observable,
      currentAssignedUser: observable,
      currentApprovalStage: observable,
      totalApprovalStages: observable,
      actionedAt: observable,
      employee: observable,
      delegateAssignmentsToUser: observable,
      assignments: observable,
      currentAssignments: observable,
      currentlyAssignedUsers: computed,
      currentStage: computed,
      datesView: computed,
      numberOfNotes: computed,
      hasValidDates: computed,
      maxEndDate: computed,
      endDateDatePickerOptions: computed,
      actionHistory: computed,
      firstStageDelegatedBy: computed,
      secondStageDelegatedBy: computed,
      clickable: computed,
      actionable: computed
    });

    this.merge(other);
  }

  get currentlyAssignedUsers() {
    return _.map(this.currentAssignments, assignment => assignment.assigneeUser);
  }

  get currentStage() {
    if (this.firstStageActionedByUser) return SECOND_APPROVAL_STAGE;
    return FIRST_APPROVAL_STAGE;
  }

  get datesView() {
    if (this.startDate.getTime() === this.endDate.getTime()) return calendarDate(this.startDate);
    return `${rangeDate(this.startDate)} – ${rangeDate(this.endDate)}`;
  }

  get numberOfNotes() {
    return _.filter([this.notes, this.firstStageNotes, this.secondStageNotes]).length;
  }

  get hasValidDates() {
    return (
      !!this.startDate &&
      !!this.endDate &&
      this.startDate.getFullYear() <= MAX_YEAR &&
      this.endDate.getFullYear() <= MAX_YEAR
    );
  }

  get maxEndDate() {
    return moment(this.startDate).add(MAX_DURATION_YEARS, 'y').subtract(1, 'd');
  }

  get endDateDatePickerOptions() {
    return {
      startDate: this.startDate,
      endDate: this.maxEndDate.format('MM/DD/YYYY')
    };
  }

  get actionHistory() {
    const history = [{
      employee: this.createdByUser,
      date: absoluteDateFormatter(this.createdAt),
      action: t('employees.profile.time_off.request.history.Submitted'),
      notes: this.notes
    }];

    if (!_.isEmpty(this.firstStageActionedByUser)) {
      history.unshift({
        employee: this.firstStageActionedByUser,
        action: this.firstStageStatus,
        date: absoluteDateFormatter(this.firstStageActionedAt),
        delegatedBy: this.firstStageDelegatedBy,
        notes: this.firstStageNotes,
        stage: 'first'
      });
    } else if (this.status === 'approved') {
      history.unshift({
        employee: this.createdByUser,
        action: t('employees.profile.time_off.request.history.Auto-Approved'),
        date: absoluteDateFormatter(this.actionedAt)
      });
    }

    if (!_.isEmpty(this.secondStageActionedByUser)) {
      history.unshift({
        employee: this.secondStageActionedByUser,
        action: this.secondStageStatus,
        date: absoluteDateFormatter(this.secondStageActionedAt),
        delegatedBy: this.secondStageDelegatedBy,
        notes: this.secondStageNotes,
        stage: 'second'
      });
    }

    return history;
  }

  get firstStageDelegatedBy() {
    return _.find(this.assignments, {
      approvalStage: 'first',
      assigneeUser: this.firstStageActionedByUser
    })?.delegatedByUser;
  }

  get secondStageDelegatedBy() {
    return _.find(this.assignments, {
      approvalStage: 'second',
      assigneeUser: this.secondStageActionedByUser
    })?.delegatedByUser;
  }

  toJS() {
    return {
      ...this,
      startDate: moment(this.startDate).format('DD/MM/YYYY'),
      endDate: moment(this.endDate).format('DD/MM/YYYY')
    };
  }

  get clickable() {
    return this.hasLink('self');
  }

  get actionable() {
    return this.hasLink('approve') || this.hasLink('reject');
  }
}

export default TimeOffRequest;
